import React from 'react'

const BUFFER_COLORS: Record<number, string> = {
  4: 'var(--color-buffer-red)',
  7: 'var(--color-buffer-purple)',
  10: 'var(--color-buffer-blue)'
}

export interface CalibrationReading {
  ph: number
  voltage_mv: number
  measured_ph: number
}

interface CalibrationChartProps {
  readings: CalibrationReading[]
}

// Calculate linear regression: x = nominal pH, y = voltage_mv
const calculateBestFitLine = (readings: CalibrationReading[]) => {
  if (readings.length < 2) return null

  const points = readings.map(r => ({ x: r.ph, y: r.voltage_mv }))

  const n = points.length
  const sumX = points.reduce((s, p) => s + p.x, 0)
  const sumY = points.reduce((s, p) => s + p.y, 0)
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0)
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0)

  const denominator = n * sumX2 - sumX * sumX
  if (denominator === 0) return null

  const slope = (n * sumXY - sumX * sumY) / denominator
  const yIntercept = (sumY - slope * sumX) / n

  return { slope, yIntercept }
}

export const CalibrationChart: React.FC<CalibrationChartProps> = ({
  readings
}) => {
  // X positions for pH 4, 7, 10 (spread wide: far left, center, far right)
  const xPcts = [10, 50, 90]

  // Get mV values
  const mvValues = readings.map(r => r.voltage_mv)

  // Compute Y-axis range with padding
  const minMv = Math.min(...mvValues)
  const maxMv = Math.max(...mvValues)
  const range = maxMv - minMv || 100
  const padding = range * 0.2
  const yMin = Math.floor((minMv - padding) / 50) * 50
  const yMax = Math.ceil((maxMv + padding) / 50) * 50
  const yRange = yMax - yMin || 100

  // Convert mV to Y percentage (0 = bottom, 100 = top)
  const mvToYPct = (mv: number) => ((mv - yMin) / yRange) * 100
  const yPcts = mvValues.map(mvToYPct)

  // Y-axis grid lines — pick ~5 evenly spaced values
  const gridStep = Math.ceil(yRange / 5 / 50) * 50
  const gridLines: number[] = []
  for (let v = yMin; v <= yMax; v += gridStep) {
    gridLines.push(v)
  }
  const labeledLines = gridLines.length <= 5 ? gridLines : [gridLines[0], gridLines[Math.floor(gridLines.length / 2)], gridLines[gridLines.length - 1]]

  // SVG viewBox dimensions
  const svgWidth = 100
  const svgHeight = 100

  // Calculate best fit line through all points
  const bestFit = calculateBestFitLine(readings)

  // Calculate line endpoints using regression (at pH 4 and pH 10)
  let lineStart = { x: xPcts[0], y: svgHeight - yPcts[0] }
  let lineEnd = { x: xPcts[2], y: svgHeight - yPcts[2] }

  if (bestFit && readings.length >= 3) {
    const mvAtPH4 = bestFit.slope * 4 + bestFit.yIntercept
    const mvAtPH10 = bestFit.slope * 10 + bestFit.yIntercept

    const yPctAtPH4 = mvToYPct(mvAtPH4)
    const yPctAtPH10 = mvToYPct(mvAtPH10)

    lineStart = { x: xPcts[0], y: svgHeight - yPctAtPH4 }
    lineEnd = { x: xPcts[2], y: svgHeight - yPctAtPH10 }
  }

  // Sort readings by pH to ensure correct order
  const sortedReadings = [...readings].sort((a, b) => a.ph - b.ph)

  return (
    <div className="w-full bg-white rounded-xl p-4 shadow-xs h-[280px] flex flex-col">
      <div className="flex-1 relative ml-10 mr-4 mt-4 mb-2 border-l border-b border-gray-200">
        {/* SVG for grid lines and data line */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient
              id="calibrationLineGradient"
              x1="10"
              y1="0"
              x2="90"
              y2="0"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#ef4444" />
              <stop offset="0.5" stopColor="#8b5cf6" />
              <stop offset="1" stopColor="#3b82f6" />
            </linearGradient>
          </defs>

          {/* Horizontal grid lines - dashed and light */}
          {gridLines.map(mv => {
            const yPos = svgHeight - mvToYPct(mv)
            return (
              <line
                key={`grid-h-${mv}`}
                x1="0"
                y1={yPos}
                x2={svgWidth}
                y2={yPos}
                stroke="#e5e7eb"
                strokeWidth={1}
                strokeDasharray="2,2"
                vectorEffect="non-scaling-stroke"
              />
            )
          })}
          {/* Vertical grid lines at buffer positions - dashed and light */}
          {xPcts.map((xPct, index) => (
            <line
              key={`grid-v-${index}`}
              x1={xPct}
              y1="0"
              x2={xPct}
              y2={svgHeight}
              stroke="#e5e7eb"
              strokeWidth={1}
              strokeDasharray="2,2"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {/* Best fit line through all points */}
          {readings.length >= 2 && (
            <line
              x1={lineStart.x}
              y1={lineStart.y}
              x2={lineEnd.x}
              y2={lineEnd.y}
              stroke="url(#calibrationLineGradient)"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>

        {/* Y-axis labels (mV) */}
        {labeledLines.map(mv => (
          <div
            key={`label-${mv}`}
            className="absolute -left-8 w-7 text-right text-xs text-gray-500"
            style={{ bottom: `${mvToYPct(mv)}%`, transform: 'translateY(50%)' }}
          >
            {mv}
          </div>
        ))}

        {/* Y-axis title */}
        <div
          className="absolute -left-10 top-1/2 text-xs text-gray-500"
          style={{ transform: 'rotate(-90deg) translateX(-50%)' }}
        >
          mV
        </div>

        {/* Data points */}
        {sortedReadings.map((reading, index) => {
          const bottomPct = mvToYPct(reading.voltage_mv)
          return (
            <div
              key={reading.ph}
              className="absolute w-3 h-3 rounded-full -ml-1.5 -mb-1.5"
              style={{
                backgroundColor:
                  BUFFER_COLORS[reading.ph as keyof typeof BUFFER_COLORS] ||
                  '#6b7280',
                left: `${xPcts[index]}%`,
                bottom: `${bottomPct}%`
              }}
            />
          )
        })}
      </div>

      {/* X-axis labels */}
      <div className="relative h-5 ml-10 mr-4">
        <span
          className="absolute text-xs text-gray-500 -translate-x-1/2"
          style={{ left: '10%' }}
        >
          pH 4
        </span>
        <span
          className="absolute text-xs text-gray-500 -translate-x-1/2"
          style={{ left: '50%' }}
        >
          pH 7
        </span>
        <span
          className="absolute text-xs text-gray-500 -translate-x-1/2"
          style={{ left: '90%' }}
        >
          pH 10
        </span>
      </div>

      {/* X-axis title */}
      <div className="text-center text-xs text-gray-500 mt-1">Buffer Solution</div>
    </div>
  )
}

export default CalibrationChart
